import React from 'react';
import { useVolatility } from '../hooks/useMarketData';
import type { TimeRange } from '../types';
import { formatPrice } from '../utils/formatters';

interface Props {
  range: TimeRange;
}

export const VolatilityIndicator: React.FC<Props> = ({ range }) => {
  const { data, isLoading, error } = useVolatility(range);

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/2 mb-4"></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="h-20 bg-gray-200 rounded"></div>
            <div className="h-20 bg-gray-200 rounded"></div>
            <div className="h-20 bg-gray-200 rounded"></div>
            <div className="h-20 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="text-red-600">Failed to load volatility data</div>
      </div>
    );
  }

  const { metrics } = data;

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">Volatility Metrics</h3>
      
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-blue-50 rounded-lg p-4">
          <p className="text-xs text-blue-600 uppercase font-medium mb-1">Standard Deviation</p>
          <p className="text-2xl font-bold text-gray-900">
            {formatPrice(metrics.standardDeviation)}
          </p>
        </div>

        <div className="bg-purple-50 rounded-lg p-4">
          <p className="text-xs text-purple-600 uppercase font-medium mb-1">Variance</p>
          <p className="text-2xl font-bold text-gray-900">
            {formatPrice(metrics.variance)}
          </p>
        </div>

        <div className="bg-green-50 rounded-lg p-4">
          <p className="text-xs text-green-600 uppercase font-medium mb-1">Avg True Range</p>
          <p className="text-2xl font-bold text-gray-900">
            {formatPrice(metrics.averageTrueRange)}
          </p>
        </div>

        <div className="bg-amber-50 rounded-lg p-4">
          <p className="text-xs text-amber-600 uppercase font-medium mb-1">Historical Vol.</p>
          <p className="text-2xl font-bold text-gray-900">
            {metrics.historicalVolatility.toFixed(2)}%
          </p>
        </div>
      </div>

      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
        <p className="text-xs text-gray-600">
          <strong>Interpretation:</strong> Higher volatility indicates larger price swings.
          Standard deviation measures price dispersion from the mean.
          ATR (Average True Range) measures market volatility by decomposing the entire range of price movements.
        </p>
      </div>
    </div>
  );
};
